import type { Scenario } from '../game/types';

/**
 * Scenario 01 -- a Jira-style sprint board for a payments team.
 *
 * Content rule for this file: every planted defect must be discoverable from
 * what the board renders on the face of the cards and headers. Nothing may
 * require opening the detail view, because on a phone that turns the game into
 * sixteen taps of tedium. The detail view is corroboration, never revelation.
 */
export const sprint42: Scenario = {
  id: 'jira-sprint-42',
  title: 'Checkout Hardening',
  subtitle: 'Payments squad · Sprint board',
  project: 'ENG',

  source: {
    toolId: 'jira',
    screen: 'Sprint board',
    kind: 'recreation',
    note: 'Not a screenshot: an original recreation in HTML that imitates the layout and conventions of a Jira board. No Atlassian code, assets or images are used.',
  },

  sprint: {
    name: 'Sprint 42',
    goal: 'Ship 3-D Secure and stop the multi-currency rounding losses.',
    velocity: 34,
    startDate: '2026-08-12',
    endDate: '2026-08-26',
    daysRemaining: 5,
  },

  people: [
    { id: 'ana', name: 'Ana Moreau', initials: 'AM', color: '#6554c0' },
    { id: 'dev', name: 'Dev Kapoor', initials: 'DK', color: '#0c66e4' },
    { id: 'sam', name: 'Sam Boyle', initials: 'SB', color: '#216e4e' },
    { id: 'jo', name: 'Jo Lindqvist', initials: 'JL', color: '#ae2e24' },
    // Left the company three weeks ago. The account still exists, so Jira will
    // happily keep it on a ticket -- which is the whole trap.
    { id: 'rae', name: 'Rae Tran', initials: 'RT', color: '#a54800', inactive: true },
  ],

  columns: [
    { id: 'backlog', name: 'Backlog', issueKeys: ['ENG-390', 'ENG-370', 'ENG-509'] },
    { id: 'todo', name: 'To Do', issueKeys: ['ENG-333', 'ENG-419', 'ENG-478', 'ENG-536'] },
    {
      id: 'inprogress',
      name: 'In Progress',
      wipLimit: 3,
      issueKeys: ['ENG-455', 'ENG-488', 'ENG-501', 'ENG-517', 'ENG-521'],
    },
    { id: 'inreview', name: 'In Review', wipLimit: 2, issueKeys: ['ENG-462', 'ENG-473'] },
    { id: 'done', name: 'Done', isDone: true, issueKeys: ['ENG-412', 'ENG-401', 'ENG-405'] },
  ],

  issues: [
    // ---------------------------------------------------------------- Backlog
    {
      key: 'ENG-390',
      type: 'bug',
      summary: 'Checkout total rounds down on multi-currency carts',
      priority: 'highest',
      labels: ['payments', 'customer-reported'],
      idleDays: 94,
      acceptanceCriteria: 2,
    },
    {
      key: 'ENG-370',
      type: 'story',
      summary: 'Persist cart across sessions',
      priority: 'medium',
      points: 5,
      idleDays: 61,
      acceptanceCriteria: 3,
    },
    {
      key: 'ENG-509',
      type: 'story',
      summary: 'Persist cart across sessions',
      priority: 'medium',
      points: 8,
      assigneeId: 'dev',
      idleDays: 9,
      acceptanceCriteria: 1,
    },

    // ------------------------------------------------------------------ To Do
    {
      key: 'ENG-333',
      type: 'epic',
      summary: 'Checkout Hardening',
      priority: 'high',
      points: 55,
      sprint: 'Sprint 42',
      assigneeId: 'ana',
      labels: ['epic'],
    },
    {
      key: 'ENG-419',
      type: 'story',
      summary: 'Add card-brand icons to the payment form',
      priority: 'low',
      points: 3,
      assigneeId: 'sam',
      sprint: 'Sprint 42',
      startDate: '2026-08-19',
      dueDate: '2026-08-05',
      acceptanceCriteria: 2,
    },
    {
      key: 'ENG-478',
      type: 'story',
      summary: 'Guest checkout email capture',
      priority: 'medium',
      estimateHours: 16,
      assigneeId: 'jo',
      sprint: 'Sprint 42',
      acceptanceCriteria: 3,
    },
    {
      key: 'ENG-536',
      type: 'story',
      summary: 'Save preferred payment method',
      priority: 'medium',
      points: 3,
      assigneeId: 'ana',
      sprint: 'Sprint 42',
      startDate: '2026-08-14',
      dueDate: '2026-08-22',
      acceptanceCriteria: 4,
    },

    // ------------------------------------------------------------ In Progress
    {
      key: 'ENG-455',
      type: 'task',
      summary: 'Upgrade payment SDK to v9',
      priority: 'high',
      points: 5,
      assigneeId: 'rae',
      sprint: 'Sprint 42',
      idleDays: 21,
      acceptanceCriteria: 2,
    },
    {
      key: 'ENG-488',
      type: 'story',
      summary: 'Retry logic for declined payments',
      priority: 'high',
      points: 8,
      sprint: 'Sprint 42',
      idleDays: 6,
      acceptanceCriteria: 3,
    },
    {
      key: 'ENG-501',
      type: 'subtask',
      summary: 'Contract tests for SDK v9',
      priority: 'medium',
      points: 2,
      assigneeId: 'dev',
      parentKey: 'ENG-455',
      sprint: 'Sprint 41',
      acceptanceCriteria: 1,
    },
    {
      key: 'ENG-517',
      type: 'story',
      summary: 'Inline validation on billing address',
      priority: 'medium',
      points: 3,
      assigneeId: 'sam',
      sprint: 'Sprint 42',
      startDate: '2026-08-18',
      dueDate: '2026-08-25',
      idleDays: 2,
      acceptanceCriteria: 3,
    },
    {
      key: 'ENG-521',
      type: 'task',
      summary: 'Instrument checkout funnel events',
      priority: 'low',
      points: 5,
      assigneeId: 'ana',
      sprint: 'Sprint 42',
      idleDays: 1,
      acceptanceCriteria: 2,
    },

    // -------------------------------------------------------------- In Review
    {
      key: 'ENG-462',
      type: 'story',
      summary: 'Webhook replay for failed captures',
      priority: 'high',
      points: 5,
      assigneeId: 'jo',
      sprint: 'Sprint 42',
      idleDays: 4,
      acceptanceCriteria: 3,
    },
    {
      key: 'ENG-473',
      type: 'story',
      summary: 'Idempotency keys on the capture endpoint',
      priority: 'high',
      points: 3,
      assigneeId: 'dev',
      sprint: 'Sprint 42',
      linkedPr: '!2841',
      idleDays: 1,
      acceptanceCriteria: 4,
    },

    // ------------------------------------------------------------------- Done
    {
      key: 'ENG-412',
      type: 'story',
      summary: 'Add 3-D Secure step to card checkout',
      priority: 'highest',
      points: 8,
      assigneeId: 'ana',
      sprint: 'Sprint 42',
      blockedBy: ['ENG-455'],
      linkedPr: '!2799',
      acceptanceCriteria: 5,
    },
    {
      key: 'ENG-401',
      type: 'task',
      summary: 'Add payment provider feature flag',
      priority: 'medium',
      points: 5,
      assigneeId: 'sam',
      sprint: 'Sprint 42',
      linkedPr: '!2790',
      acceptanceCriteria: 2,
    },
    {
      key: 'ENG-405',
      type: 'story',
      summary: 'Card expiry field UX',
      priority: 'low',
      points: 3,
      assigneeId: 'jo',
      sprint: 'Sprint 42',
      linkedPr: '!2802',
      acceptanceCriteria: 3,
    },
  ],

  defects: [
    {
      id: 'd-overcommit',
      target: { kind: 'sprint', id: 'Sprint 42' },
      title: 'Sprint committed at triple the team velocity',
      severity: 'critical',
      explanation:
        'The team’s established velocity is 34 points, but 106 points are committed to Sprint 42 with 5 days left. A commitment this far past the evidence is not a stretch goal — it is a plan that is already known to be false, and it destroys the sprint’s value as a forecast.',
      fix: 'Cut scope to roughly one velocity’s worth of work and move the rest back to the backlog. If the number was inflated by the epic roll-up, fix that first and re-measure.',
    },
    {
      id: 'd-done-blocked',
      target: { kind: 'issue', id: 'ENG-412' },
      altTargets: [{ kind: 'issue', id: 'ENG-455' }],
      title: 'Done ticket still blocked by unfinished work',
      severity: 'critical',
      explanation:
        'ENG-412 sits in Done while declaring "is blocked by ENG-455", and ENG-455 is still In Progress. Either the 3-D Secure work shipped without the SDK upgrade it depends on, or the board is lying about what is actually finished. Both are serious, and the second one silently corrupts every burndown and release note built from this board.',
      fix: 'Establish which is true. If the dependency is real, pull ENG-412 back out of Done; if it is stale, delete the link. Then add a workflow condition that blocks the transition to Done while an open blocker exists.',
    },
    {
      id: 'd-wip-breach',
      target: { kind: 'column', id: 'inprogress' },
      title: 'WIP limit exceeded and being ignored',
      severity: 'major',
      explanation:
        'In Progress is configured with a WIP limit of 3 and holds 5 tickets. A WIP limit that is routinely blown is worse than no limit at all: the team gets the red highlight, learns to ignore it, and loses the one signal that was supposed to trigger a conversation about finishing work before starting more.',
      fix: 'Stop starting, start finishing — drive the column back under 3. If 3 genuinely is not workable for the team size, raise the limit deliberately rather than letting it be violated by default.',
    },
    {
      id: 'd-inactive-assignee',
      target: { kind: 'issue', id: 'ENG-455' },
      title: 'Assigned to a deactivated account',
      severity: 'major',
      explanation:
        'ENG-455 is assigned to Rae Tran, whose account is deactivated — they have left the company. The ticket has not moved in 21 days, which is exactly what you would expect. Nobody is doing this work, but the board shows it as actively in progress and someone downstream is depending on it.',
      fix: 'Reassign it now. Then add a board filter or automation rule that flags any open issue assigned to an inactive user, because this recurs every time somebody leaves.',
    },
    {
      id: 'd-unassigned-wip',
      target: { kind: 'issue', id: 'ENG-488' },
      title: 'In Progress with no assignee',
      severity: 'major',
      explanation:
        'Work cannot be in progress if no one is doing it. An unassigned ticket in an active column means either somebody is working on it without claiming it — so nobody else knows — or the ticket was dragged across in a stand-up and forgotten. It has been idle 6 days, which suggests the latter.',
      fix: 'Assign it or move it back to To Do. A workflow validator requiring an assignee on the In Progress transition prevents the whole class of problem.',
    },
    {
      id: 'd-stale-p1',
      target: { kind: 'issue', id: 'ENG-390' },
      title: 'Highest-priority customer bug abandoned in the backlog',
      severity: 'major',
      explanation:
        'A customer-reported bug that loses money on every multi-currency cart is marked Highest priority and has sat untouched for 94 days. Either the priority is wrong or the neglect is wrong — they cannot both be right. When the top priority level demonstrably means nothing, the whole priority field stops carrying information.',
      fix: 'Schedule it or downgrade it honestly. If highest-priority items are not being pulled into sprints, the triage process is broken and no amount of relabelling will fix that.',
    },
    {
      id: 'd-epic-on-board',
      target: { kind: 'issue', id: 'ENG-333' },
      title: 'Epic sitting in a sprint column, double-counting its own children',
      severity: 'major',
      explanation:
        'ENG-333 is an epic — a container for the very stories sitting beside it on this board — yet it has been dropped into To Do carrying a 55-point roll-up estimate. Those points are counted once in the epic and again in each child, which is most of why this sprint appears to be committed at 106 points.',
      fix: 'Take the epic off the sprint board. Epics are tracked on the roadmap or a dedicated epic panel; only their children belong in a sprint.',
    },
    {
      id: 'd-duplicate',
      target: { kind: 'issue', id: 'ENG-370' },
      altTargets: [{ kind: 'issue', id: 'ENG-509' }],
      title: 'Duplicate tickets with conflicting estimates',
      severity: 'minor',
      explanation:
        'ENG-370 and ENG-509 describe the same work — "Persist cart across sessions" — filed twice. They carry different estimates (5 points versus 8) and only one has an assignee, so refinement has now happened twice with different answers. Whichever gets scheduled, the other resurfaces later as phantom scope.',
      fix: 'Close one as a duplicate and link it to the survivor. Keep the better-refined description rather than automatically keeping the older key.',
    },
    {
      id: 'd-date-inversion',
      target: { kind: 'issue', id: 'ENG-419' },
      title: 'Due date falls before the start date',
      severity: 'minor',
      explanation:
        'ENG-419 starts on 19 Aug and is due on 5 Aug — two weeks before it begins. This is almost always a copy-paste or a date-picker slip, but it quietly poisons anything that consumes dates: timeline views, overdue reports, and any automation that escalates late work.',
        fix: 'Correct the dates, and add a field validator so the save is rejected when due precedes start.',
    },
    {
      id: 'd-hours-on-points-board',
      target: { kind: 'issue', id: 'ENG-478' },
      title: 'Hour estimate on a story-point board',
      severity: 'minor',
      explanation:
        'Every other ticket on this board is estimated in story points; ENG-478 carries 16 hours and no points. Because the board sums points, this ticket contributes zero to the sprint total — it is real work that is invisible to the forecast, and mixing two estimation units means neither number can be trusted.',
      fix: 'Pick one unit and enforce it. Re-estimate ENG-478 in points and remove the hour field from the board’s configured estimation statistic.',
    },
    {
      id: 'd-orphan-sprint',
      target: { kind: 'issue', id: 'ENG-501' },
      title: 'Subtask stranded in the previous sprint',
      severity: 'minor',
      explanation:
        'ENG-501 is a subtask of ENG-455, but it is still assigned to Sprint 41 while its parent moved to Sprint 42. Subtasks that drift from their parent get missed at sprint close, distort both sprints’ numbers, and make the parent look closer to done than it is.',
      fix: 'Move it to Sprint 42 with its parent. Most teams should simply not put subtasks on the sprint board independently of the parent.',
    },
    {
      id: 'd-review-no-pr',
      target: { kind: 'issue', id: 'ENG-462' },
      title: 'In Review with nothing to review',
      severity: 'minor',
      explanation:
        'ENG-462 is in the In Review column but has no linked pull request — every other reviewed ticket on this board has one. There is nothing for a reviewer to open, so the ticket occupies a review slot while no review can actually happen.',
      fix: 'Link the PR, or move the ticket back until one exists. A smart-commit convention or a workflow condition requiring a linked PR makes this self-enforcing.',
    },
  ],
};
